import { Link, Typography } from "@mui/material"
import { Link as RouterLink } from "@tanstack/react-router"
import type { ColumnDef } from "@tanstack/react-table"

import { OrderStatusChip } from "../../PurchaseOrder/components/OrderStatusChip"
import { ShipStatusFlatChip } from "../../PurchaseOrder/components/ShipStatusFlatChip"
import { PurchaseOrderActions } from "../components/PurchaseOrderActions"

import DateChip from "@/components/DateChip"
import type { OrderLine, OrderStatus, ShipStatus } from "@/graphql/codegen/graphql"
import { ProductOrderPriceUomColumn } from "@/screens/Procurement/PurchaseOrder/ProductAdded/components/ProductOrderPriceUomColumn"

export const getProductOrderColumns = ({
  onUploadClick,
  canUpload,
}: {
  onUploadClick: (orderId: string) => void
  canUpload: boolean
}): ColumnDef<OrderLine>[] => [
  {
    header: "Product ID",
    accessorKey: "productId",
    enableSorting: true,
    cell: ({ row }) => <Typography component='span'>{row.original.productId}</Typography>,
  },
  {
    header: "Description",
    accessorKey: "companyProductDescription",
    enableSorting: true,
    cell: ({ row }) => <Typography component='span'>{row.original.product?.companyProductDescription}</Typography>,
  },
  {
    header: "PO No.",
    accessorKey: "orderId",
    enableSorting: true,
    cell: ({ row }) => (
      <Link
        component={RouterLink}
        to={`/procurement/purchase-orders/${row.original.orderDetail?.orderId}`}
        className='text-tertiary-base no-underline'
      >
        {row.original?.orderDetail?.orderId}
      </Link>
    ),
  },
  {
    header: "Status",
    accessorKey: "status",
    enableSorting: true,
    cell: ({ row }) => <OrderStatusChip status={row.original.orderDetail?.order?.status as OrderStatus} />,
  },
  {
    header: "Ship Status",
    accessorKey: "shipStatus",
    enableSorting: true,
    cell: ({ row }) => <ShipStatusFlatChip shipStatus={row.original.orderDetail?.order?.shipStatus as ShipStatus} />,
  },
  {
    header: "Requested",
    accessorKey: "orderDetail.order.requestedDeliveryDate",
    enableSorting: true,
    cell: ({ getValue }) => {
      const date = getValue<string>()
      return <DateChip date={date} className='text-tertiary-base' color='primary' />
    },
  },
  {
    header: "Expected",
    accessorKey: "orderDetail.order.expectedDeliveryDate",
    enableSorting: true,
    cell: ({ getValue }) => {
      const date = getValue<string>()
      return <DateChip date={date} className='text-tertiary-base' color='primary' />
    },
  },
  {
    header: "Price/UoM",
    accessorKey: "uom",
    cell: ({ row }) => <ProductOrderPriceUomColumn row={row} />,
    size: 200,
    enableSorting: false,
  },
  {
    id: "expand",
    header: "",
    cell: ({ row }) => (
      <PurchaseOrderActions
        row={row}
        purchaseOrderId={row.original.orderDetail?.orderId as number}
        onUploadClick={onUploadClick}
        canUpload={canUpload}
        orderType={row.original.orderDetail?.order?.type}
      />
    ),
    enableSorting: false,
  },
]
